import { useEffect, useRef } from 'react';
import { X, AlertTriangle, CheckCircle, Info, AlertOctagon } from 'lucide-react';

const ICONS = {
  error: AlertOctagon,
  warning: AlertTriangle,
  success: CheckCircle,
  info: Info,
};

const COLORS = {
  error: { bg: 'var(--danger-bg)', border: 'var(--danger)', text: 'var(--danger)' },
  warning: { bg: 'var(--warning-bg)', border: 'var(--warning)', text: 'var(--warning)' },
  success: { bg: 'var(--success-bg)', border: 'var(--success)', text: 'var(--success)' },
  info: { bg: 'var(--primary-bg)', border: 'var(--primary)', text: 'var(--primary)' },
};

const AUTO_DISMISS_MS = {
  error: 8000,
  warning: 6000,
  success: 4000,
  info: 5000,
};

function ToastItem({ toast, onDismiss }) {
  const ref = useRef(null);
  const dismissedRef = useRef(false);
  const type = toast.type || 'error';
  const Icon = ICONS[type];
  const colors = COLORS[type];

  useEffect(() => {
    const timer = setTimeout(() => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      const el = ref.current;
      if (el) {
        el.classList.remove('toast-enter');
        el.classList.add('toast-exit');
        setTimeout(() => onDismiss(toast.id), 300);
      } else {
        onDismiss(toast.id);
      }
    }, AUTO_DISMISS_MS[type]);
    return () => clearTimeout(timer);
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = () => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    const el = ref.current;
    if (el) {
      el.classList.remove('toast-enter');
      el.classList.add('toast-exit');
      setTimeout(() => onDismiss(toast.id), 300);
    } else {
      onDismiss(toast.id);
    }
  };

  return (
    <div
      ref={ref}
      className="toast-item toast-enter"
      style={{
        background: colors.bg,
        borderColor: colors.border,
      }}
    >
      <Icon size={18} style={{ color: colors.text, flexShrink: 0, marginTop: 1 }} />
      <div className="toast-content">
        {toast.title && (
          <div className="toast-title" style={{ color: colors.text }}>
            {toast.title}
          </div>
        )}
        <div className="toast-message">{toast.message}</div>
        {toast.code && (
          <div className="toast-code">{toast.code}</div>
        )}
      </div>
      <button className="toast-close" onClick={handleDismiss}>
        <X size={14} />
      </button>
    </div>
  );
}

export default function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
